import { useRequest } from 'features/request/useRequest';
import { ActKc2StatusEnum } from 'gql/graphql';
import { replacerCostIfValue } from 'utils/replacerCost';

import { useCallback, useMemo } from 'react';
import { useDispatch } from 'react-redux';

import { gqlSetActKC2PlanVolume, gqlSetActKC2AcceptedVolume } from '../workOrderQueries';

export const useMethodsErrorHandlingModal = (
    actKC2Id: number,
    workOrderPriceId: number,
    defaultValue: string,
    actKC2Status: ActKc2StatusEnum,
) => {
    const dispatch = useDispatch();

    const isKC2StatusInProgress = actKC2Status === ActKc2StatusEnum.InProgress;
    const isKC2StatusInOnCheck = actKC2Status === ActKc2StatusEnum.OnCheck;

    const [actKC2PlanVolumeData, actKC2PlanVolumeStatus, actKC2PlanVolumeError, actKC2PlanVolume] = useRequest({
        requestId: 'actKC2PlanVolume',
        query: gqlSetActKC2PlanVolume,
        withEffect: false,
    });

    const [actKC2AcceptedVolumeData, actKC2AcceptedVolumeStatus, actKC2AcceptedVolumeError, actKC2AcceptedVolume] =
        useRequest({
            requestId: 'actKC2AcceptedVolume',
            query: gqlSetActKC2AcceptedVolume,
            withEffect: false,
        });

    const handleOnChange = useCallback(
        (value: string) => {
            if (isKC2StatusInProgress) {
                dispatch(
                    actKC2PlanVolume({
                        actKC2Id,
                        workOrderPriceId,
                        volume: replacerCostIfValue(value),
                    }),
                );
            }

            if (isKC2StatusInOnCheck) {
                dispatch(
                    actKC2AcceptedVolume({
                        actKC2Id,
                        workOrderPriceId,
                        volume: replacerCostIfValue(value),
                    }),
                );
            }
        },
        [
            actKC2AcceptedVolume,
            actKC2Id,
            actKC2PlanVolume,
            dispatch,
            isKC2StatusInOnCheck,
            isKC2StatusInProgress,
            workOrderPriceId,
        ],
    );

    const getRequestData = useMemo(() => {
        if (isKC2StatusInProgress) {
            return actKC2PlanVolumeData?.uerSetActKC2PlanVolume;
        }

        return actKC2AcceptedVolumeData?.uerSetActKC2AcceptedVolume;
    }, [
        isKC2StatusInProgress,
        actKC2PlanVolumeData?.uerSetActKC2PlanVolume,
        actKC2AcceptedVolumeData?.uerSetActKC2AcceptedVolume,
    ]);

    const getRequestError = useMemo(() => {
        if (isKC2StatusInProgress) {
            return actKC2PlanVolumeError;
        }

        return actKC2AcceptedVolumeError;
    }, [isKC2StatusInProgress, actKC2PlanVolumeError, actKC2AcceptedVolumeError]);

    const getRequestStatus = useMemo(() => {
        if (isKC2StatusInProgress) {
            return actKC2PlanVolumeStatus;
        }

        return actKC2AcceptedVolumeStatus;
    }, [isKC2StatusInProgress, actKC2PlanVolumeStatus, actKC2AcceptedVolumeStatus]);

    return {
        handleOnChange,
        getRequestData,
        getRequestError,
        getRequestStatus,
    };
};
